import { Box } from '@mentimeter/ragnar-ui/box';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import type {
  EmptyT,
  InteractiveCanvasComponents,
  RatingT,
} from '@mentimeter/question-modules-types';
import type { RatingResultT } from '@mentimeter/visualizations';
import type { QuestionDimension, QuestionGrid } from '@mentimeter/http-clients';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { ScreenReaderTable } from './ScreenReaderTable';
import { LegacyRatingGridDataHandler } from './LegacyRatingGrid';
import { RatingVisualization } from './RatingVisualization';
import { transformResults } from './utils/transformResults';

interface RatingChoice {
  id: number;
  label: string;
}

interface RatingPresentationProps {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2;
  shouldHideResults: boolean;
  shouldAnimate: boolean;
  results: RatingT | EmptyT;
  choices: RatingChoice[];
  visualizationType: 'two-values' | 'four-values';
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  isNewLookFlagEnabled: boolean;
  range: {
    min: number;
    max: number;
  };
  xAxisLabels: {
    axisLabel: string;
    minimumValueLabel: string;
    maximumValueLabel: string;
  };
  yAxisLabels: {
    axisLabel: string;
    minimumValueLabel: string;
    maximumValueLabel: string;
  };

  /** @deprecated Left here temporarily until the old visualization dies, do not use it for the new visualization */
  grid: QuestionGrid;

  backgroundImage:
    | {
        url: string;
      }
    | {
        imageKey: string;
        imageRotation: number;
      }
    | null;
}

export const RatingPresentation = ({
  title,
  styledTitle,
  label,
  longerDescription,
  fontSizeOffset,
  shouldHideResults,
  shouldAnimate,
  results,
  choices,
  CanvasVisualizationArea,
  visualizationType,
  isNewLookFlagEnabled,

  range,
  grid,
  backgroundImage,
  xAxisLabels,
  yAxisLabels,
}: RatingPresentationProps) => {
  const resultsWithFallback = choices.map((choice, index) => {
    const result = results.results.find(({ id }) => id === choice.id);
    const fallbackResult: RatingResultT = {
      id: choice.id,
      label: choice.label,
      score: [null, null],
    };
    return { ...(result ?? fallbackResult), index };
  });

  const ConditionalWrapper = CanvasVisualizationArea ?? Box;

  const questionDimensions: [QuestionDimension, QuestionDimension] = [
    {
      label: xAxisLabels.axisLabel,
      min: xAxisLabels.minimumValueLabel,
      max: xAxisLabels.maximumValueLabel,
    },
    {
      label: yAxisLabels.axisLabel,
      min: yAxisLabels.minimumValueLabel,
      max: yAxisLabels.maximumValueLabel,
    },
  ];

  const transformedResults = transformResults({ results });

  return (
    <Box
      width="100%"
      height="100%"
      display="flex"
      alignItems="center"
      data-testid="rating-container"
      justifyContent="center"
    >
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        description={longerDescription}
        label={label}
        fontSizeOffset={fontSizeOffset}
        showTextSizeDropdown={true}
      />
      <ConditionalWrapper flex="1 1 auto" width="100%">
        {isNewLookFlagEnabled ? (
          <RatingVisualization
            shouldAnimate={shouldAnimate}
            shouldHideResults={shouldHideResults}
            results={transformedResults}
            choices={choices}
            range={range}
            backgroundImage={backgroundImage}
            horizontalAxisLabel={xAxisLabels.axisLabel}
            verticalAxisLabel={yAxisLabels.axisLabel}
          />
        ) : (
          <LegacyRatingGridDataHandler
            dimensions={questionDimensions}
            range={range}
            subType={visualizationType}
            grid={grid}
            results={{
              results: resultsWithFallback,
              votes: results.type === 'rating' ? results.votes : [],
              respondents: results.respondents,
            }}
            enableAnimations={shouldAnimate}
          />
        )}
      </ConditionalWrapper>
      <ScreenReaderOnly>
        <ScreenReaderTable
          results={resultsWithFallback}
          shouldHideResults={shouldHideResults}
          range={range}
          dimensions={questionDimensions}
        />
      </ScreenReaderOnly>
    </Box>
  );
};
